import React, { useMemo } from 'react';
import { Tabs, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { Card } from '@cards/Card';
import './styles.less';

function CareTeamPermissionsCard({ loading, pathway }) {
  const { t } = useTranslation();

  const allAvailableRules = useMemo(() => {
    if (!pathway) return [];
    const stagedRules = pathway.stages?.flatMap(stage => stage.rules || []) || [];
    const indexEventRules = pathway.indexEvents?.flatMap(ie => ie.rules || []) || [];
    return [...stagedRules, ...indexEventRules];
  }, [pathway]);

  const availableRuleIds = useMemo(() => new Set(allAvailableRules.map(r => r.id)), [
    allAvailableRules,
  ]);

  const availableContentRefs = useMemo(
    () => new Set(allAvailableRules.map(r => r.whatDetail?.content).filter(Boolean)),
    [allAvailableRules],
  );

  const callToActionMap = useMemo(() => {
    const map = new Map();
    const ctaList = pathway?.clientConfiguration?.callToAction || [];
    ctaList.forEach(cta => map.set(cta.id, cta));
    return map;
  }, [pathway]);

  const roles = useMemo(() => {
    if (pathway && pathway.careTeamPermissions) {
      return pathway.careTeamPermissions;
    }
    if (pathway && pathway.roles) {
      const stagedRules = pathway.stages.flatMap(stage => stage.rules);
      const indexEventRules = pathway.indexEvents.flatMap(indexEvent => indexEvent.rules);
      const pathwayRules = [...stagedRules, ...indexEventRules];
      const rolesRules = pathway.roles.map(role => ({
        ...role,
        permissions: role.permissions.map(permission => ({
          ...permission,
          rule: permission.type === 'rule' ? pathwayRules.find(rule => rule.id === permission.id) : undefined,
          callToAction: permission.type === 'cta' ? callToActionMap.get(permission.id) : undefined,
        })),
      }));

      return rolesRules;
    }

    return [];
  }, [pathway, callToActionMap]);

  const isCTA = record => record.type === 'cta' || (!record.type && record.callToAction);
  const isRule = record => record.type === 'rule' || (!record.type && record.rule);

  const columns = [
    {
      title: t('pathways:ProcedurePathways.wizard.review.careTeamRules.columns.itemName'),
      dataIndex: 'rule',
      key: 'itemName',
      width: 300,
      ellipsis: true,
      render: (rule, record) => {
        if (isCTA(record)) {
          return record.callToAction?.name;
        }

        if (isRule(record) && record.id && !availableRuleIds.has(record.id)) {
          return (
            <p className="rule-status-text">
              {t('pathways:ProcedurePathways.wizard.careTeamRules.ruleRemoved')}
            </p>
          );
        }
        return rule?.name;
      },
    },
    {
      title: t('pathways:ProcedurePathways.wizard.review.careTeamRules.columns.value'),
      dataIndex: 'rule',
      key: 'value',
      render: (rule, record) => {
        if (isCTA(record)) {
          return record.contentRef || record.callToAction?.value;
        }

        if (isRule(record) && record.id && !availableRuleIds.has(record.id)) {
          return null;
        }

        if (isRule(record) && record.contentRef && !availableContentRefs.has(record.contentRef)) {
          return (
            <p className="rule-status-text">
              {t('pathways:ProcedurePathways.wizard.careTeamRules.contentMismatch')}
            </p>
          );
        }
        return rule?.whatDetail?.name || rule?.whatDetail?.latestVersion?.name;
      },
    },
    {
      title: t('pathways:ProcedurePathways.wizard.review.careTeamRules.columns.audience'),
      dataIndex: 'rule',
      key: 'audience',
      width: 120,
      render: (rule, record) => {
        let audience;

        if (isCTA(record)) {
          audience = record.callToAction?.audience;
        } else {
          audience = rule?.audienceType;
        }

        if (!audience) return null;

        const audienceKey = `pathways:ProcedurePathways.wizard.careTeamRules.callToAction.audience.${audience}`;
        return t(audienceKey);
      },
    },
    {
      title: t('pathways:ProcedurePathways.wizard.review.careTeamRules.columns.permissionType'),
      dataIndex: 'permissionType',
      key: 'permissionType',
      width: 200,
      render: permissionType => {
        const permissionTypeKey = `pathways:ProcedurePathways.wizard.review.careTeamRules.permissionTypes.${permissionType}`;
        return t(permissionTypeKey);
      },
    },
  ];

  if (!pathway || !roles || roles.length === 0) return null;

  return (
    <Card.Half
      className="role-rules-card"
      loading={loading}
      title={t('pathways:ProcedurePathways.wizard.review.careTeamRules.header')}
    >
      <Tabs>
        {roles.map((role, index) => {
          const dataSource = role.permissions.map(permission => ({
            ...permission,
            key: permission.id || permission.rule?.id,
          }));

          return (
            <Tabs.TabPane tab={role.name} key={`role-${index}`}>
              {role.description && <p className="role-description">{role.description}</p>}
              <Table
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                rowKey={record => record.id || record.rule?.id}
              />
            </Tabs.TabPane>
          );
        })}
      </Tabs>
    </Card.Half>
  );
}

export default CareTeamPermissionsCard;
